/**
 * Favorites on every Flow tab (Signals, Companies, People, Plays). One source of truth for the favorite value
 * shapes (DB JSON and wire); types are derived via `z.infer` only. The file keeps its Signals-era name because the
 * setting key (`signalsHeadlineChips`) lives here and the stored rows predate the other tabs.
 */
import { z } from 'zod';
import { SIGNAL_LANDING_PRESET_ID } from './signal-view';
import { MAX_VIEW_ID_LENGTH } from './view-state';

/** Setting key used for BOTH the dashboard_setting (global) and user_setting (personal) rows. */
export const SIGNAL_CHIPS_SETTING_KEY = 'signalsHeadlineChips';
/** Server advisory-lock keys, kept beside the setting key they guard. */
export const SIGNAL_CHIPS_GLOBAL_LOCK_KEY = `${SIGNAL_CHIPS_SETTING_KEY}:global`;
export const signalChipsPersonalLockKey = (userId: number): string => `${SIGNAL_CHIPS_SETTING_KEY}:user:${userId}`;
export const SIGNAL_CHIP_LABEL_MAX = 40;

/** The fixed, non-editable catch-all tile after the Signals favorites: opens My Signals minus the favorites' rows. */
export const SIGNAL_CHIPS_ALL_ELSE = {
  viewId: SIGNAL_LANDING_PRESET_ID,
  label: 'All else',
  description: 'Your to-dos that none of the favorites above include.',
} as const;

/**
 * The 400 the Signals list returns for `exclude_favorites=1` when a favorite filters on a browser-evaluated
 * field (Open business hours) and so can't be excluded server-side. The client matches on `code` to recover.
 */
export const ALL_ELSE_UNEXCLUDABLE = {
  code: 'FAVORITES_UNEXCLUDABLE',
  title: "Favorites can't be excluded: a favorite filters on Open business hours",
} as const;

/**
 * The chip label a view name seeds: trimmed, capped at the label max, and trimmed again so the cap can't leave a
 * trailing space. The single rule for the chip editor's pre-fill and the server's rename sync.
 */
export const chipLabelForViewName = (name: string): string => name.trim().slice(0, SIGNAL_CHIP_LABEL_MAX).trim();

/** Per-user chip size preference (user_setting, keyed by the real signed-in human). One size for every tab. */
export const SIGNAL_CHIPS_SIZE_SETTING_KEY = 'signalsHeadlineChipsSize';

export const SignalChipSizeSchema = z.enum(['large', 'small']);
export type SignalChipSize = z.infer<typeof SignalChipSizeSchema>;

/** The one documented default: no stored row means the chips show expanded. */
export const DEFAULT_SIGNAL_CHIP_SIZE: SignalChipSize = 'large';

export const SignalChipsSizeSettingSchema = z.object({ size: SignalChipSizeSchema });
export type SignalChipsSizeSetting = z.infer<typeof SignalChipsSizeSettingSchema>;

/** `.strict()` so a body can never carry a user selector. */
export const SignalChipSizeWriteBodySchema = z.object({ size: SignalChipSizeSchema }).strict();
export type SignalChipSizeWriteBody = z.infer<typeof SignalChipSizeWriteBodySchema>;

export const SignalChipSizeResponseSchema = z.object({ size: SignalChipSizeSchema });
export type SignalChipSizeResponse = z.infer<typeof SignalChipSizeResponseSchema>;

// Favorite items

/** The tabs whose favorites point at a saved/preset view — every tab, Plays included. */
export const FAVORITE_VIEW_ENTITIES = ['signal', 'account', 'contact', 'play'] as const;
export const FavoriteViewEntitySchema = z.enum(FAVORITE_VIEW_ENTITIES);
export type FavoriteViewEntity = z.infer<typeof FavoriteViewEntitySchema>;

/** The four tabs that carry favorites (the same list as the view entities; kept as its own name for tab-keyed consumers). */
export const FavoriteEntitySchema = z.enum(FAVORITE_VIEW_ENTITIES);
export type FavoriteEntity = z.infer<typeof FavoriteEntitySchema>;

/**
 * The retired pinned-play kind. Stored `{ entity: 'play', playId }` items are re-tagged to it on read: readable and
 * deletable, never creatable (`FavoriteInputSchema` has no `playId`). Shown on the Plays tab.
 */
export const FAVORITE_RETIRED_PLAY_ENTITY = 'play_pinned' as const;

/** Tab names as reps see them, for copy like "This view is a favorite on Companies". */
export const FAVORITE_TAB_LABEL: Record<FavoriteEntity, string> = {
  signal: 'Signals',
  account: 'Companies',
  contact: 'People',
  play: 'Plays',
};

/** Per-tab limits, enforced on add (server, under the lock) — never on read. */
export const FAVORITES_GLOBAL_MAX = 8;
export const FAVORITES_PERSONAL_MAX = 5;

export const FavoriteScopeSchema = z.enum(['global', 'personal']);
export type FavoriteScope = z.infer<typeof FavoriteScopeSchema>;

/** Server-generated `crypto.randomUUID()`; 64 so legacy ids (`legacy-global-1`) also fit. */
export const FavoriteIdSchema = z.string().min(1).max(64);

const favoriteLabel = z.string().trim().min(1).max(SIGNAL_CHIP_LABEL_MAX);
const favoriteViewTarget = {
  entity: FavoriteViewEntitySchema,
  viewId: z.string().min(1).max(MAX_VIEW_ID_LENGTH),
  label: favoriteLabel,
};
export const FavoriteViewItemSchema = z.object({ id: FavoriteIdSchema, ...favoriteViewTarget });
export type FavoriteViewItem = z.infer<typeof FavoriteViewItemSchema>;

/** A stored pinned play (pre-saved-views Plays favorite), re-tagged on read. Read-only: no input branch creates it. */
export const FavoriteRetiredPlayItemSchema = z.object({
  id: FavoriteIdSchema,
  entity: z.literal(FAVORITE_RETIRED_PLAY_ENTITY),
  playId: z.string().min(1).max(64),
  label: favoriteLabel,
});
export type FavoriteRetiredPlayItem = z.infer<typeof FavoriteRetiredPlayItemSchema>;

export const FavoriteItemSchema = z.discriminatedUnion('entity', [FavoriteViewItemSchema, FavoriteRetiredPlayItemSchema]);
export type FavoriteItem = z.infer<typeof FavoriteItemSchema>;

/** The id of what a favorite points at: the play id for a retired pinned play, the view id for everything else. */
export const favoriteTargetId = (item: FavoriteItem): string =>
  item.entity === FAVORITE_RETIRED_PLAY_ENTITY ? item.playId : item.viewId;

/** The tab an item shows on: a retired pinned play belongs to Plays. The ONE place client and server group by tab. */
export const favoriteTabOf = (item: Pick<FavoriteItem, 'entity'>): FavoriteEntity =>
  item.entity === FAVORITE_RETIRED_PLAY_ENTITY ? 'play' : item.entity;

// Stored settings (dashboard_setting global row / user_setting personal row)

/** Old chip position of a legacy slot index: global slots were chips 1-4, personal slots chips 5-6. */
const LEGACY_FIRST_POSITION: Record<FavoriteScope, number> = { global: 1, personal: 5 };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reads a #2289 tuple row (`{ slots: [slot | null, ...] }`, no `items`) as the new list: each non-null slot becomes a
 * Signals favorite whose id records its old chip position (`legacy-global-<1..4>` / `legacy-personal-<5..6>`); nulls
 * vanish. Anything else passes through untouched. No migration — the next write stores the new shape.
 */
const convertLegacySlots = (scope: FavoriteScope, value: unknown): unknown => {
  if (!isRecord(value) || 'items' in value || !Array.isArray(value.slots)) return value;
  const { slots, ...rest } = value;
  const slotList: unknown[] = slots;
  const items = slotList.flatMap((slot, index) =>
    isRecord(slot)
      ? [{ id: `legacy-${scope}-${index + LEGACY_FIRST_POSITION[scope]}`, entity: 'signal', viewId: slot.viewId, label: slot.label }]
      : [],
  );
  return { ...rest, items };
};

/**
 * Re-tags every stored pinned play (`{ entity: 'play', playId: string }`) as the retired `play_pinned` kind. Keyed on
 * `playId` alone, so an item carrying both `playId` and `viewId` also becomes `play_pinned` (its `viewId` is then
 * stripped by the item schema) rather than failing the whole row. Lossless and idempotent: already-`play_pinned` items
 * and `{ entity: 'play', viewId }` view favorites pass through. Never mutates the input.
 */
const retagPinnedPlays = (value: unknown): unknown => {
  if (!isRecord(value) || !Array.isArray(value.items)) return value;
  const items: unknown[] = value.items;
  return {
    ...value,
    items: items.map((item) =>
      isRecord(item) && item.entity === 'play' && typeof item.playId === 'string'
        ? { ...item, entity: FAVORITE_RETIRED_PLAY_ENTITY }
        : item,
    ),
  };
};

/** Read-side upgrade of a stored favorites row: `{slots}` tuple → items, then pinned plays → `play_pinned`. */
export const legacyFavoritesPreprocess =
  (scope: FavoriteScope) =>
  (value: unknown): unknown =>
    retagPinnedPlays(convertLegacySlots(scope, value));

/**
 * Deliberately NO `.max()` on `items`: an over-limit row must still parse. A parse failure makes the settings
 * service fall back to empty, and the next write would then overwrite the real row.
 */
export const FavoritesGlobalSettingSchema = z.preprocess(
  legacyFavoritesPreprocess('global'),
  z.object({ items: z.array(FavoriteItemSchema), updatedByUserId: z.number().int().nullable() }),
);
export type FavoritesGlobalSetting = z.infer<typeof FavoritesGlobalSettingSchema>;

export const FavoritesPersonalSettingSchema = z.preprocess(legacyFavoritesPreprocess('personal'), z.object({ items: z.array(FavoriteItemSchema) }));
export type FavoritesPersonalSetting = z.infer<typeof FavoritesPersonalSettingSchema>;

/** The "unset" fallback: empty is the initial state, NOT a product default. */
export const EMPTY_FAVORITES_GLOBAL: FavoritesGlobalSetting = { items: [], updatedByUserId: null };
export const EMPTY_FAVORITES_PERSONAL: FavoritesPersonalSetting = { items: [] };

// Request bodies

/**
 * POST/PUT body: a view item minus its server-generated `id`. `.strict()` so a body can never carry a user selector,
 * a client-chosen id, or a `playId` (pinned plays are retired: readable and deletable, never created).
 */
export const FavoriteInputSchema = z.object(favoriteViewTarget).strict();
export type FavoriteInput = z.infer<typeof FavoriteInputSchema>;

// Wire

/** Per-item state the server adds on GET. `targetMissing`: the view was deleted, or the item is a retired pinned play. */
const favoriteWireExtras = { scope: FavoriteScopeSchema, editable: z.boolean(), targetMissing: z.boolean() };

export const FavoriteWireSchema = z.discriminatedUnion('entity', [
  FavoriteViewItemSchema.extend(favoriteWireExtras),
  FavoriteRetiredPlayItemSchema.extend(favoriteWireExtras),
]);
export type FavoriteWire = z.infer<typeof FavoriteWireSchema>;

/** No length pin: the list is variable per tab and per scope. */
export const FavoritesResponseSchema = z.object({
  canEditGlobal: z.boolean(),
  viewAs: z.object({ sfUserId: z.string(), name: z.string().nullable() }).nullable(),
  size: SignalChipSizeSchema,
  items: z.array(FavoriteWireSchema),
});
export type FavoritesResponse = z.infer<typeof FavoritesResponseSchema>;

/** The tabs whose favorites carry counts. Plays never has counts, so `play` (and `play_pinned`) are rejected. */
export const FavoriteCountsEntitySchema = z.enum(['signal', 'account', 'contact']);
export type FavoriteCountsEntity = z.infer<typeof FavoriteCountsEntitySchema>;

/** Whether a tab gets favorite counts — the client's one check before requesting `/favorites/counts`. */
export const isCountsEntity = (entity: FavoriteEntity): entity is FavoriteCountsEntity => FavoriteCountsEntitySchema.safeParse(entity).success;

export const FavoriteCountsResponseSchema = z.object({
  /** Counted tabs only: `play` is rejected (see `FavoriteCountsEntitySchema`). */
  entity: FavoriteCountsEntitySchema,
  /** `viewId` is the view the count was taken for, so a client can drop a count whose favorite has since changed. */
  counts: z.array(z.object({ id: FavoriteIdSchema, viewId: z.string(), count: z.number().int().nullable() })),
  /**
   * The Signals "All else" tile's count: rows in My Signals matched by none of this tab's favorites (overlaps count
   * once). `null` for the other tabs, or when a favorite filters on a field the server cannot count.
   */
  allElseCount: z.number().int().nullable(),
});
export type FavoriteCountsResponse = z.infer<typeof FavoriteCountsResponseSchema>;
